import classNames from "classnames";
import { useTranslations } from "next-intl";
import { Control, Controller } from "react-hook-form";

import Icon from "~/components/common/icon";
import Checkbox from "~/components/form/checkbox";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";

import editorialCardsStyles from "./editorial-cards.module.scss";
import styles from "./filter-cards-desktop.module.scss";

type Props = {
  cardFilter: Editorials.EditorialCards["cardFilter"];
  quantityFilters: Record<string, number>;
  scrollUp: boolean;
  activeFilters: string[];
  control: Control<any>;
  handleReset: () => void;
  handlePartialReset: (fields: string[]) => void;
};

export default function FilterCardsDesktop({
  cardFilter,
  activeFilters,
  scrollUp,
  control,
  handleReset,
  handlePartialReset,
  quantityFilters,
}: Props) {
  const filterEditorialTag = Constants.FILTER_CARDS_STANDARD.editorial;
  const filterVideoTag = Constants.FILTER_CARDS_STANDARD.video;

  const t = useTranslations();

  return (
    <div className={classNames(styles.sidebar, scrollUp ? styles.scrollUp : undefined)}>
      <div className={styles.filterBoxHighlighted}>
        <div className={styles.filterBoxHighlightedTitle}>
          <Icon name="filters" width={16} height={16} />
          <p>{t("generic.filters")}</p>
          {activeFilters.length > 0 && (
            <span className={editorialCardsStyles.highlightValue}>{activeFilters.length}</span>
          )}
        </div>
        {activeFilters.length > 0 && (
          <button type="button" className={editorialCardsStyles.clearButton} onClick={() => handleReset()}>
            {t("generic.clear_all")}
          </button>
        )}
      </div>

      <div className={editorialCardsStyles.filterBox}>
        <div className={editorialCardsStyles.wrapperClearButton}>
          <p className={editorialCardsStyles.filterBoxName}>{t("generic.tutorialType")}</p>
          <button
            className={classNames(
              editorialCardsStyles.clearButton,
              activeFilters.find((filter) => filter === filterVideoTag || filter === filterEditorialTag)
                ? editorialCardsStyles.show
                : editorialCardsStyles.hide
            )}
            onClick={() => handlePartialReset([filterVideoTag, filterEditorialTag])}
          >
            {t("generic.clear")}
          </button>
        </div>

        {quantityFilters[filterEditorialTag] && (
          <div className={editorialCardsStyles.filterBoxCheck}>
            <Controller
              name={filterEditorialTag}
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  label={t("generic.editorial")}
                  checked={activeFilters.includes(filterEditorialTag)}
                />
              )}
            />
            <span>{`(${quantityFilters[filterEditorialTag]})`}</span>
          </div>
        )}
        {quantityFilters[filterVideoTag] && (
          <div className={editorialCardsStyles.filterBoxCheck}>
            <Controller
              name={filterVideoTag}
              control={control}
              render={({ field }) => (
                <Checkbox {...field} label={t("generic.video")} checked={activeFilters.includes(filterVideoTag)} />
              )}
            />
            <span>{`(${quantityFilters[filterVideoTag]})`}</span>
          </div>
        )}
      </div>

      {cardFilter &&
        cardFilter.length > 0 &&
        cardFilter.map((filter, i) => {
          const atLeastOneSelected = activeFilters.find((activeFilter) => filter.tags.includes(activeFilter));

          if (!filter.tags.find((tag) => quantityFilters[tag])) {
            return null;
          }

          return (
            <div key={filter.name + "-" + i} className={editorialCardsStyles.filterBox}>
              <div className={editorialCardsStyles.wrapperClearButton}>
                <p className={editorialCardsStyles.filterBoxName}>{filter.name}</p>
                <button
                  className={classNames(
                    editorialCardsStyles.clearButton,
                    atLeastOneSelected ? editorialCardsStyles.show : editorialCardsStyles.hide
                  )}
                  onClick={() => handlePartialReset(filter.tags)}
                >
                  {t("generic.clear")}
                </button>
              </div>
              {filter.tags.map((tag) => {
                if (!quantityFilters[tag]) {
                  return null;
                }

                return (
                  <div key={tag} className={editorialCardsStyles.filterBoxCheck}>
                    <Controller
                      name={tag}
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} label={t(`generic.${tag}`)} checked={activeFilters.includes(tag)} />
                      )}
                    />
                    <span>{`(${quantityFilters[tag]})`}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
    </div>
  );
}
